/**
 * The `@webflow/XscpData` clipboard shape.
 *
 * Transcribed from real payloads and cross-checked against Webstudio's zod schema
 * (`packages/project-build/src/runtime/webflow/schema.ts`), which is the best open-source
 * reference for this format.
 *
 * ⚠️ This format is undocumented and unsupported by Webflow, and it has drifted at least
 * twice: the 2019 envelope had no `payload` wrapper at all, and `meta` has since gained two
 * counters. `fixtures/provenance/` holds real captures precisely so a third drift fails a
 * test here rather than surprising a marketer mid-paste.
 */

export interface XscpTextNode {
  _id: string;
  text: true;
  v: string;
}

export interface XscpElementNode {
  _id: string;
  type: string;
  tag: string;
  classes: string[];
  children: string[];
  data: XscpNodeData;
  /** On HtmlEmbed this carries the raw HTML. */
  v?: string;
}

export interface XscpNodeData {
  tag?: string;
  text?: boolean;
  attr?: Record<string, string>;
  xattr?: Array<{ name: string; value: string }>;
  displayName?: string;
  search?: { exclude: boolean };
  visibility?: { conditions: unknown[]; keepInHtml?: { tag: string; val: Record<string, unknown> } };
  devlink?: { runtimeProps: Record<string, unknown>; slot: string };
  link?: Record<string, unknown>;
  grid?: { type: string };

  /**
   * On an Image: the Webflow asset id, matching an `assets[]._id`.
   *
   * Note this is SEPARATE from `attr.src`, which carries a plain absolute URL — the
   * `@img_<assetId>` token form appears only inside `styleLess` for background images, never
   * here. For an image with no Webflow asset record this is `{}` and `assets` stays empty,
   * which the paste handler tolerates.
   */
  img?: { id?: string };

  /** On an HtmlEmbed. `meta.html` duplicates the node's top-level `v`, byte for byte. */
  embed?: {
    type: 'html';
    meta: {
      html: string;
      div: boolean;
      iframe: boolean;
      /** True when the HTML contains a <script>. This is what `w-embed w-script` means. */
      script: boolean;
      compilable: boolean;
    };
  };

  /** On an HtmlEmbed: whether it sits inside a rich text element. */
  insideRTE?: boolean;
}

export type XscpNode = XscpElementNode | XscpTextNode;

export interface XscpStyle {
  _id: string;
  fake: false;
  type: 'class';
  name: string;
  namespace: string;
  /** `"&"` marks a combo class; `""` a base class. */
  comb: string;
  styleLess: string;
  variants: Record<string, { styleLess: string }>;
  /** `_id`s of combo classes derived from this one. */
  children: string[];
  createdBy?: string;
  origin: null;
  selector: null;
}

export interface XscpPayload {
  nodes: XscpNode[];
  styles: XscpStyle[];
  assets: unknown[];
  /** Legacy Interactions 1.0. Always empty. */
  ix1: unknown[];
  /** We never synthesise interactions — neither export path can create IX3. */
  ix2: { interactions: unknown[]; events: unknown[]; actionLists: unknown[] };
}

/** Webflow's copy-side report of what it stripped. Informational; zeros are fine. */
export interface XscpMeta {
  unlinkedSymbolCount: number;
  droppedLinks: number;
  dynBindRemovedCount: number;
  dynListBindRemovedCount: number;
  paginationRemovedCount: number;
  universalBindingsRemovedCount: number;
  codeComponentsRemovedCount: number;
}

export interface XscpDocument {
  type: '@webflow/XscpData';
  payload: XscpPayload;
  meta: XscpMeta;
}

export function isTextNode(n: XscpNode): n is XscpTextNode {
  return (n as XscpTextNode).text === true;
}
